import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { AudioAssetDto, Project } from "../api/types";
import { useReplaceJobs } from "../lib/replaceJobs";
import { useFeature } from "../lib/features";
import { Icon } from "./icons";
import { toast } from "./toast";

type Work = "keep" | "reproof" | "drop";

/**
 * Replacing a project's file set.
 *
 * The judgement is made on three things the reviewer can see for themselves -
 * the file's NAME, its RUNTIME and its FORMAT - so every verdict on this screen
 * can be checked at a glance and overruled with confidence. A verdict nobody
 * can audit still has to be trusted, which is worse than no verdict at all.
 *
 * All three are readable in the browser, so NOTHING IS UPLOADED UNTIL THE
 * REVIEWER ACCEPTS. That ordering is the whole fix for the worst thing the old
 * flow did: it uploaded first and matched second, so cancelling the review left
 * the new files sitting in the project beside the ones they were meant to
 * replace.
 *
 * Bringing the accepted files in is handed to lib/replaceJobs, which runs it
 * app-wide: its dialog takes over from this one, and can be hidden.
 */

type Phase = "pick" | "reading" | "review";

interface Judgement {
  verdict: "match" | "likely" | "confirm" | "decide";
  label: string;
  score: number;
  name: string;
  runtime: string;
  format: string;
  default_new: boolean;
  detail: string;
  accepted: boolean;
}

interface Row {
  incoming: string;
  seconds: number | null;
  runtime: string;
  format: string;
  current: string | null;
  current_shown: string | null;
  current_runtime: string | null;
  judgement: Judgement | null;
  detail: string;
  rivals: { filename: string; shown: string; score: number }[];
}

interface Candidate {
  filename: string;
  shown: string;
  runtime: string;
}

interface MatchResult {
  rows: Row[];
  kept: Candidate[];
  candidates: Candidate[];
}

const TONE: Record<string, string> = { match: "ok", likely: "ok", confirm: "warn", decide: "mute" };

/** The file's runtime, read from its own header before it goes anywhere.
 *  This is the whole reason the review can happen before the upload. */
function readRuntime(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement("audio");
    let settled = false;
    const done = (v: number | null) => {
      if (settled) return;
      settled = true;
      URL.revokeObjectURL(url);
      resolve(v);
    };
    // A format the browser cannot decode leaves the runtime unknown rather
    // than stalling the review; it then counts as "different", which asks the
    // reviewer rather than guessing on their behalf.
    const bail = window.setTimeout(() => done(null), 15000);
    el.preload = "metadata";
    el.onloadedmetadata = () => {
      window.clearTimeout(bail);
      done(Number.isFinite(el.duration) && el.duration > 0 ? el.duration : null);
    };
    el.onerror = () => { window.clearTimeout(bail); done(null); };
    el.src = url;
  });
}

export function ReplaceFileSetDialog({ projectId, assets, onClose }: {
  projectId: string;
  assets: AudioAssetDto[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [phase, setPhase] = useState<Phase>("pick");
  const [files, setFiles] = useState<File[]>([]);
  const [drag, setDrag] = useState(false);
  const [done, setDone] = useState(0);
  const [total, setTotal] = useState(0);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState<MatchResult | null>(null);
  /** What each incoming file replaces. null = bring it in as a new file. */
  const [choice, setChoice] = useState<Record<string, string | null>>({});
  /** The verdict for the CHOSEN pairing, which changes when the reviewer does. */
  const [judged, setJudged] = useState<Record<string, Judgement | null>>({});
  /** Accepted. Match and Likely Match arrive ticked; Confirm and Decide are
   *  named for what they ask of the reviewer, so they arrive empty. */
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  /** What becomes of the replaced file's proofing and pickups: keep both, keep
   *  the pickups and proof the new audio again ("reproof"), or drop both. */
  const [work, setWork] = useState<Record<string, Work>>({});
  const canReproof = useFeature("replace_reproof");
  const anchor = useRef<number | null>(null);
  const cancelled = useRef(false);
  const jobs = useReplaceJobs();

  // Reset on the way IN as well as out: React's development double-invoke
  // mounts, unmounts and remounts, and a cleanup-only guard latches true on
  // that first unmount and never clears.
  useEffect(() => {
    cancelled.current = false;
    return () => { cancelled.current = true; };
  }, []);

  const byName = useMemo(() => {
    const m: Record<string, File> = {};
    for (const f of files) m[f.name] = f;
    return m;
  }, [files]);

  const pick = (list: FileList | File[]) => {
    const audio = Array.from(list).filter((f) => /\.(wav|mp3|m4a|flac|ogg|webm)$/i.test(f.name));
    if (!audio.length) { toast("No accepted audio files.", "error"); return; }
    setFiles(audio);
  };

  const run = useCallback(async () => {
    setPhase("reading"); setBusy(""); setDone(0); setTotal(files.length);
    try {
      // The delivery's own runtimes, read locally. No upload, no cost.
      const incoming: { name: string; seconds: number | null }[] = [];
      for (let i = 0; i < files.length; i++) {
        if (cancelled.current) return;
        setNote(files[i].name);
        incoming.push({ name: files[i].name, seconds: await readRuntime(files[i]) });
        setDone(i + 1);
      }

      // Anything already here that has never been measured. Usually nothing:
      // a spec check measures every file it touches.
      const plan = await api<{ measure: string[] }>(`/api/projects/${projectId}/replace/plan`, { method: "POST" });
      if (plan.measure.length) {
        setDone(0); setTotal(plan.measure.length); setBusy("Reading the runtimes already here");
        for (let i = 0; i < plan.measure.length; i++) {
          if (cancelled.current) return;
          setNote(plan.measure[i]);
          try {
            await api(`/api/projects/${projectId}/replace/measure`, {
              method: "POST", body: JSON.stringify({ filename: plan.measure[i] }),
            });
          } catch { /* an unreadable file just stays unmeasured */ }
          setDone(i + 1);
        }
      }

      if (cancelled.current) return;
      const res = await api<MatchResult>(`/api/projects/${projectId}/replace/match`, {
        method: "POST", body: JSON.stringify({ incoming }),
      });
      if (cancelled.current) return;

      const nextChoice: Record<string, string | null> = {};
      const nextJudged: Record<string, Judgement | null> = {};
      const nextTicked = new Set<string>();
      const nextWork: Record<string, Work> = {};
      for (const r of res.rows) {
        const asNew = !r.current || (r.judgement?.default_new ?? false);
        nextChoice[r.incoming] = asNew ? null : r.current;
        nextJudged[r.incoming] = r.judgement;
        // A file whose length changed was re-edited: the old proofing's times
        // no longer point where they did, so proofing it again is the default.
        nextWork[r.incoming] = canReproof && r.judgement?.runtime === "different" ? "reproof" : "keep";
        if (!asNew && r.judgement?.accepted) nextTicked.add(r.incoming);
      }
      setChoice(nextChoice); setJudged(nextJudged);
      setTicked(nextTicked); setWork(nextWork);
      setResult(res); setPhase("review");
    } catch (e: any) {
      toast(e?.message || "Could not read the delivery.", "error");
      setPhase("pick");
    }
  }, [files, projectId]);

  /** Re-judge a pairing the reviewer picked themselves. A hand-picked pair is
   *  a question, and deserves the same answer an automatic one gets. */
  const repick = async (name: string, target: string | null) => {
    // One existing file can be replaced by ONE incoming file. Without this two
    // rows could point at the same slot, and applying would stack both onto it
    // - two new versions of one chapter out of a single delivery, while the
    // file the other row should have replaced quietly kept its old audio.
    const released = target
      ? Object.entries(choice).filter(([other, t]) => other !== name && t === target).map(([other]) => other)
      : [];
    setChoice((c) => {
      const next = { ...c, [name]: target };
      released.forEach((other) => { next[other] = null; });
      return next;
    });
    if (released.length) {
      setJudged((j) => {
        const next = { ...j };
        released.forEach((other) => { next[other] = null; });
        return next;
      });
      // Taking a file back off another row is a real change to that row, so it
      // stops being accepted until somebody looks at it again.
      setTicked((t) => {
        const next = new Set(t);
        released.forEach((other) => next.delete(other));
        return next;
      });
      const shown = result?.candidates.find((c) => c.filename === target)?.shown ?? target;
      toast(`${shown} was matched with ${released[0]} — that row is back to New File.`, "info");
    }
    if (!target) { setJudged((j) => ({ ...j, [name]: null })); return; }
    const file = byName[name];
    try {
      const res = await api<{ judgement: Judgement }>(`/api/projects/${projectId}/replace/compare`, {
        method: "POST",
        body: JSON.stringify({
          incoming: { name, seconds: result?.rows.find((r) => r.incoming === name)?.seconds ?? null },
          current: target,
        }),
      });
      setJudged((j) => ({ ...j, [name]: res.judgement }));
    } catch {
      if (file) setJudged((j) => ({ ...j, [name]: null }));
    }
  };

  /** Ticking, with the ranges a file list is expected to have: click sets one
   *  and anchors, Shift extends the anchor's run, Cmd/Ctrl toggles one. */
  const tick = (index: number, e: React.MouseEvent<HTMLInputElement>) => {
    const rows = result?.rows ?? [];
    const name = rows[index].incoming;
    const want = !ticked.has(name);
    const next = new Set(ticked);
    if (e.shiftKey && anchor.current !== null) {
      const [lo, hi] = anchor.current < index ? [anchor.current, index] : [index, anchor.current];
      for (let k = lo; k <= hi; k++) {
        if (want) next.add(rows[k].incoming); else next.delete(rows[k].incoming);
      }
    } else {
      if (want) next.add(name); else next.delete(name);
      anchor.current = index;
    }
    setTicked(next);
  };

  const allTicked = !!result?.rows.length && result.rows.every((r) => ticked.has(r.incoming));
  const tickAll = () => {
    anchor.current = null;
    setTicked(allTicked ? new Set() : new Set((result?.rows ?? []).map((r) => r.incoming)));
  };

  /** Which incoming file has claimed each existing one. */
  const claimedBy = useMemo(() => {
    const m = new Map<string, string>();
    for (const [incoming, current] of Object.entries(choice)) {
      if (current) m.set(current, incoming);
    }
    return m;
  }, [choice]);

  /** The candidates nobody has claimed. A row's OWN pick is not among them -
   *  it is rendered above the groups, so the closed select reads as an answer. */
  const freeFor = (_incoming: string) =>
    (result?.candidates ?? []).filter((c) => !claimedBy.has(c.filename));

  /** The ones another row has taken, and who took them. */
  const takenFor = (incoming: string) => {
    const out: { c: Candidate; by: string }[] = [];
    for (const c of result?.candidates ?? []) {
      const by = claimedBy.get(c.filename);
      if (by && by !== incoming) out.push({ c, by });
    }
    return out;
  };

  const accepted = useMemo(
    () => (result?.rows ?? []).filter((r) => ticked.has(r.incoming)),
    [result, ticked],
  );

  // From here the work runs app-wide (lib/replaceJobs), and its own dialog -
  // the same progress, plus Hide - takes over from this one.
  const apply = () => {
    if (!accepted.length) { toast("Tick the files you want to bring in.", "error"); return; }
    const rows = accepted.filter((r) => byName[r.incoming]);
    jobs.start(
      projectId,
      qc.getQueryData<Project>(["project", projectId])?.name ?? "",
      rows.map((r) => ({
        name: r.incoming,
        size: byName[r.incoming].size,
        current: choice[r.incoming] ?? null,
        seconds: r.seconds,
        apply_work: (work[r.incoming] ?? "keep") !== "drop",
        work: work[r.incoming] ?? "keep",
      })),
      rows.map((r) => byName[r.incoming]),
    );
    jobs.show(projectId);
    onClose();
  };

  const counts = useMemo(() => {
    const rows = result?.rows ?? [];
    const of = (v: string) => rows.filter((r) => judged[r.incoming]?.verdict === v && choice[r.incoming]).length;
    return {
      sure: of("match") + of("likely"),
      ask: of("confirm"),
      decide: rows.filter((r) => !choice[r.incoming] || judged[r.incoming]?.verdict === "decide").length,
    };
  }, [result, judged, choice]);

  const working = phase === "reading";

  /** Keep or drop the proofing for every row that replaces a file, in one go.
   *  `on` only when every such row already agrees, so a mixed list shows as
   *  mixed rather than claiming a choice nobody made. */
  const replacing = (result?.rows ?? []).filter((r) => choice[r.incoming]);
  const allWork = (w: Work) =>
    replacing.length > 0 && replacing.every((r) => (work[r.incoming] ?? "keep") === w);
  const workAll = (w: Work) =>
    setWork((c) => {
      const next = { ...c };
      replacing.forEach((r) => { next[r.incoming] = w; });
      return next;
    });

  return (
    <div className="rfs-scrim" onClick={(e) => { if (e.target === e.currentTarget && phase === "pick") onClose(); }}>
      <div className={`rfs${phase === "review" ? " wide" : ""}`}>
        <div className="rfs-head">
          <div>
            <strong>Replace file set</strong>
            <span>
              {phase === "review"
                ? `${result?.rows.length ?? 0} new files against the ${result?.candidates.length ?? assets.length} in this project. Tick what you accept - Shift-click for a run, Cmd-click for one.`
                : "Bring in a corrected delivery. The files you have now are kept and stay one click away."}
            </span>
          </div>
          {phase === "review" && (
            <div className="rfs-chips">
              {counts.sure > 0 && <span className="rfs-chip ok">{counts.sure} matched</span>}
              {counts.ask > 0 && <span className="rfs-chip warn">{counts.ask} to confirm</span>}
              {counts.decide > 0 && <span className="rfs-chip mute">{counts.decide} to decide</span>}
            </div>
          )}
        </div>

        {phase === "pick" && (
          <div className="rfs-body">
            <div className={`dropzone${drag ? " drag" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files); }}
              onClick={() => document.getElementById("rfs-input")?.click()}>
              <input id="rfs-input" type="file" multiple accept="audio/*" hidden
                onChange={(e) => { if (e.target.files) pick(e.target.files); e.currentTarget.value = ""; }} />
              {files.length
                ? <><strong>{files.length} file{files.length > 1 ? "s" : ""} ready</strong>
                    <div className="hint">{files.slice(0, 3).map((f) => f.name).join(", ")}{files.length > 3 ? ` and ${files.length - 3} more` : ""}</div></>
                : <><strong>Drop the corrected files here</strong>
                    <div className="hint">Matched on name, runtime and format. Nothing is uploaded until you accept it.</div></>}
            </div>
          </div>
        )}

        {working && (
          <div className="rfs-body">
            <div className="rfs-progress">
              <div className="rfs-bar"><span style={{ width: `${total ? (done / total) * 100 : 0}%` }} /></div>
              <div className="rfs-prog-text">
                <span>{busy || "Reading the delivery"}</span>
                <span className="mono">{done} / {total}</span>
              </div>
              {note && <div className="rfs-prog-file mono">{note}</div>}
            </div>
          </div>
        )}

        {phase === "review" && result && (
          <div className="rfs-table">
            <div className="rfs-row rfs-th">
              <span><input type="checkbox" checked={allTicked} onChange={tickAll} aria-label="Accept every file" /></span>
              <span>New file</span><span /><span>Replaces</span>
              <span>Verdict</span>
              <span>
                Proofing &amp; pickups
                {replacing.length > 1 && (
                  <span className="rfs-seg" style={{ display: "flex", width: "fit-content", marginTop: 5, textTransform: "none", letterSpacing: 0 }}>
                    <button type="button" className={allWork("keep") ? "on" : ""}
                      title="Keep the proofing and pickups on every file that replaces one"
                      onClick={() => workAll("keep")}>Keep all</button>
                    {canReproof && (
                      <button type="button" className={allWork("reproof") ? "on" : ""}
                        title="Keep the pickups and proof every replacement's new audio again"
                        onClick={() => workAll("reproof")}>Re-proof all</button>
                    )}
                    <button type="button" className={allWork("drop") ? "on" : ""}
                      title="Drop the proofing and pickups on every file that replaces one"
                      onClick={() => workAll("drop")}>Drop all</button>
                  </span>
                )}
              </span>
            </div>
            {result.rows.map((row, i) => {
              const target = choice[row.incoming] ?? null;
              const j = judged[row.incoming];
              const tone = target && j ? TONE[j.verdict] ?? "mute" : "mute";
              const stale = j?.runtime === "different";
              const w = work[row.incoming] ?? "keep";
              return (
                <div key={row.incoming} className={`rfs-row tone-${tone}${ticked.has(row.incoming) ? " picked" : ""}`}>
                  <span>
                    <input type="checkbox" checked={ticked.has(row.incoming)}
                      onChange={() => { /* driven by onClick, which carries the modifiers */ }}
                      onClick={(e) => tick(i, e)}
                      aria-label={`Accept ${row.incoming}`} />
                  </span>
                  <span>
                    <b className="mono">{row.incoming}</b>
                    <em className="dim mono">{row.runtime} · {row.format}</em>
                  </span>
                  <span className="rfs-arrow"><Icon name="chevron-right" size={14} /></span>
                  <span>
                    <select className="rfs-pick" value={target ?? ""}
                      onChange={(e) => repick(row.incoming, e.target.value || null)}>
                      <option value="">New File</option>
                      {/* This row's own pick sits at the top, so the closed
                          select reads as the answer rather than as a group. */}
                      {target && (
                        <option value={target}>
                          {result.candidates.find((c) => c.filename === target)?.shown ?? target}
                        </option>
                      )}
                      {/* Split, because the list is scanned once per row and
                          what matters each time is what is still FREE. Working
                          down a delivery, the taken half grows and the half you
                          actually have to read shrinks. */}
                      {freeFor(row.incoming).length > 0 && (
                        <optgroup label="Not matched yet">
                          {freeFor(row.incoming).map((c) => (
                            <option key={c.filename} value={c.filename}>{c.shown}</option>
                          ))}
                        </optgroup>
                      )}
                      {takenFor(row.incoming).length > 0 && (
                        <optgroup label="Already matched">
                          {takenFor(row.incoming).map(({ c, by }) => (
                            <option key={c.filename} value={c.filename}>{c.shown} — with {by}</option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                    {target && <em className="dim mono">{result.candidates.find((c) => c.filename === target)?.runtime}</em>}
                  </span>
                  <span className="rfs-ev">
                    {target && j
                      ? <><b className={`rfs-badge ${tone}`}>{j.label}</b><em>{j.detail}</em></>
                      : <><b className="rfs-badge mute">New File</b><em className="dim">Added on its own, replacing nothing</em></>}
                  </span>
                  <span className="rfs-work">
                    {!target ? <em className="dim">nothing to apply</em> : (
                      <>
                        <span className="rfs-seg">
                          <button type="button" className={w === "keep" ? "on" : ""}
                            title="Keep the proofing and the pickups as they are"
                            onClick={() => setWork((c) => ({ ...c, [row.incoming]: "keep" }))}>Keep</button>
                          {canReproof && (
                            <button type="button" className={w === "reproof" ? "on" : ""}
                              title="Keep the pickups and proof the new audio again: its new flags join them for another review"
                              onClick={() => setWork((c) => ({ ...c, [row.incoming]: "reproof" }))}>Re-proof</button>
                          )}
                          <button type="button" className={w === "drop" ? "on" : ""}
                            title="Leave the proofing and pickups with the file being replaced"
                            onClick={() => setWork((c) => ({ ...c, [row.incoming]: "drop" }))}>Drop</button>
                        </span>
                        {w === "reproof" && <em className="dim">Pickups kept · new audio proofed again</em>}
                        {stale && w === "keep" && (
                          <em className="warn">{canReproof
                            ? "Runtime changed: its times won't line up. Re-proof is safer"
                            : "Runtime changed, so it will be flagged for re\u2011proof"}</em>
                        )}
                      </>
                    )}
                  </span>
                </div>
              );
            })}
            {result.kept.map((u) => (
              <div key={u.filename} className="rfs-row tone-mute">
                <span />
                <span className="dim"><em>not supplied</em></span>
                <span className="rfs-arrow dim">–</span>
                <span><b className="mono dim">{u.shown}</b><em className="dim mono">{u.runtime}</em></span>
                <span className="rfs-ev"><b className="rfs-badge mute">Kept</b><em className="dim">Nothing in the new set replaces it</em></span>
                <span className="rfs-work"><em className="dim">untouched</em></span>
              </div>
            ))}
          </div>
        )}

        <div className="rfs-foot">
          <div className="rfs-foot-text">
            {phase === "review" && result
              ? <>
                  <span>
                    {accepted.length} of {result.rows.length} accepted
                    {" · "}{accepted.filter((r) => choice[r.incoming]).length} replacing a file
                    {" · "}{accepted.filter((r) => !choice[r.incoming]).length} added on their own
                  </span>
                  <em>Nothing is uploaded until you press the button. The files you have now are kept either way.</em>
                </>
              : <em>Nothing is transcribed and nothing is charged.</em>}
          </div>
          <div className="rfs-actions">
            <button className="btn" onClick={onClose}>Cancel</button>
            {phase === "pick" && (
              <button className="btn primary" disabled={!files.length} onClick={run}>
                Match {files.length || ""} file{files.length === 1 ? "" : "s"}
              </button>
            )}
            {phase === "review" && (
              <button className="btn primary" disabled={!accepted.length} onClick={apply}>
                Bring in {accepted.length || ""} file{accepted.length === 1 ? "" : "s"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
